import { MoreHorizontalIcon } from 'lucide-react'
import { Fragment, type ReactNode, useState } from 'react'

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './dropdown-menu'

export interface DataTableRowAction {
  label: string
  onSelect: () => void | Promise<void>
  href?: string
  disabled?: boolean
  loading?: boolean
  destructive?: boolean
  primary?: boolean
  icon?: ReactNode
  /**
   * The accessible name, when the visible label alone does not say which
   * record the control acts on.
   *
   * The primary inline control uses `label` as its accessible name — so a grid
   * of rows announces "Deactivate, Deactivate, Deactivate" with nothing to
   * tell the rows apart. `label`
   * stays short because the row supplies the context to a sighted reader; a
   * screen-reader user has no row. Set this to a string that names the
   * record, for example "Close branch JED".
   *
   * `meta.actionsLabel` names the trailing "..." trigger, not the inline action.
   *
   * Optional, and undefined emits no attribute — every caller written before
   * this field keeps the visible label as its accessible name.
   */
  ariaLabel?: string
  /** The `data-testid` on the rendered control. Undefined emits no attribute. */
  testId?: string
}

function DataTableActions({
  actions,
  label,
  phoneRows = false,
}: {
  actions: DataTableRowAction[]
  phoneRows?: boolean
  // Falls back to the joined action labels so every existing caller keeps
  // working unchanged; a caller setting `meta.actionsLabel` gets a label that
  // names the row instead of one identical across every row in the grid.
  label?: string
}) {
  if (actions.length === 0) return null
  const primary = actions.find((action) => action.primary && !action.destructive)
  const menuActions = actions.filter((action) => action !== primary)
  const menuItems = [
    ...menuActions.filter((action) => !action.destructive),
    ...menuActions.filter((action) => action.destructive),
  ]
  // A link is underlined at rest, 1px, and 2px on hover: with ink as the
  // accent, colour alone cannot tell a link from text.
  const linkClassName = `px-2 text-sm font-medium text-link underline decoration-1 underline-offset-[3px] hover:decoration-2 aria-disabled:text-muted-foreground aria-disabled:no-underline${phoneRows ? ' @max-[40rem]:inline-flex @max-[40rem]:min-h-10 @max-[40rem]:items-center' : ''}`
  // Disabled is recoloured, never faded (DESIGN.md "Buttons").
  const iconActionClassName = `h-control-compact inline-flex items-center gap-1.5 rounded-sm border border-input bg-card px-2.5 text-sm font-medium text-foreground transition-ui hover:bg-accent active:translate-y-px disabled:cursor-not-allowed disabled:border-border disabled:bg-muted disabled:text-muted-foreground disabled:active:translate-y-0${phoneRows ? ' @max-[40rem]:min-h-10' : ''}`

  if (menuActions.length === 0) {
    return (
      <span className="inline-flex w-full items-center justify-end">
        {primary ? (
          <ActionControl
            action={primary}
            className={primary.icon ? iconActionClassName : linkClassName}
          />
        ) : null}
      </span>
    )
  }

  const menu = (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={label ?? actions.map((action) => action.label).join(', ')}
          className={`inline-flex size-8 items-center justify-center rounded-sm text-muted-foreground transition-ui hover:bg-accent hover:text-foreground data-[state=open]:bg-accent data-[state=open]:text-foreground${phoneRows ? ' @max-[40rem]:size-10' : ''}`}
        >
          <MoreHorizontalIcon className="size-4" aria-hidden="true" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {menuItems.map((action, index) => (
          <Fragment key={action.label}>
            {index > 0 && action.destructive && !menuItems[index - 1]?.destructive ? (
              <DropdownMenuSeparator />
            ) : null}
            <DropdownMenuItem
              aria-label={action.ariaLabel}
              disabled={action.disabled || action.loading}
              variant={action.destructive ? 'destructive' : 'default'}
              onClick={action.href ? undefined : () => action.onSelect()}
              asChild={Boolean(action.href)}
            >
              {action.href ? (
                <a
                  href={action.href}
                  aria-label={action.ariaLabel}
                  aria-disabled={action.disabled || action.loading || undefined}
                  aria-busy={action.loading || undefined}
                  onClick={(event) => {
                    if (action.disabled || action.loading) {
                      event.preventDefault()
                      return
                    }
                    action.onSelect()
                  }}
                >
                  {action.label}
                </a>
              ) : (
                action.label
              )}
            </DropdownMenuItem>
          </Fragment>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
  return (
    <span className="inline-flex w-full items-center justify-end gap-1">
      {primary ? (
        <ActionControl
          action={primary}
          className={primary.icon ? iconActionClassName : linkClassName}
        />
      ) : null}
      {menu}
    </span>
  )
}

function ActionControl({ action, className }: { action: DataTableRowAction; className: string }) {
  const [pending, setPending] = useState(false)
  const loading = action.loading || pending

  function select() {
    if (loading) return

    setPending(true)
    try {
      const result = action.onSelect()
      void Promise.resolve(result).finally(() => setPending(false))
    } catch (error) {
      setPending(false)
      throw error
    }
  }

  const icon = <ActionIcon action={action} loading={loading} />

  if (action.href) {
    return (
      <a
        href={action.href}
        aria-label={action.ariaLabel}
        data-testid={action.testId}
        aria-disabled={action.disabled || loading || undefined}
        aria-busy={loading || undefined}
        onClick={(event) => {
          event.stopPropagation()
          if (action.disabled || loading) {
            event.preventDefault()
            return
          }
          select()
        }}
        className={className}
      >
        {icon}
        {action.label}
      </a>
    )
  }

  return (
    <button
      type="button"
      aria-label={action.ariaLabel}
      data-testid={action.testId}
      aria-disabled={action.disabled || loading || undefined}
      aria-busy={loading || undefined}
      disabled={action.disabled || loading}
      onClick={(event) => {
        event.stopPropagation()
        // A button has no navigation to prevent — guarding on `disabled` and
        // returning is the button-shaped equivalent of the anchor branch's
        // `preventDefault`.
        if (action.disabled || loading) return
        select()
      }}
      className={className}
    >
      {icon}
      {action.label}
    </button>
  )
}

function ActionIcon({ action, loading }: { action: DataTableRowAction; loading: boolean }) {
  if (loading) {
    return (
      <span
        aria-hidden="true"
        data-slot="action-spinner"
        className="size-3 animate-spin border-[1.5px] border-current border-e-transparent"
      />
    )
  }

  return action.icon ? <span aria-hidden="true">{action.icon}</span> : null
}

export { DataTableActions }
